/**
 * Terminal 82 - 3D Candle Component
 *
 * Creates a rotating 3D candlestick with accurate OHLC proportions.
 */

import type { OHLC, RarityTier } from '../types';
import { calculateCandleDimensions } from './utils';

// ============================================
// 3D Candle Builder
// ============================================

export interface CandleOptions {
  ohlc: OHLC;
  changePercent: number;
  tier?: RarityTier;
}

/**
 * Create 3D candle HTML structure with accurate OHLC proportions
 */
export function createCandle3D(options: CandleOptions): string {
  const { ohlc, changePercent, tier = 'common' } = options;

  const dims = calculateCandleDimensions(ohlc, changePercent, tier);
  const halfDepth = dims.depth / 2;

  // CSS custom properties for dynamic sizing
  const cssVars = `
    --candle-height: ${dims.bodyHeight}px;
    --candle-width: ${dims.width}px;
    --candle-depth: ${dims.depth}px;
    --candle-half-depth: ${halfDepth}px;
    --candle-side-width: ${dims.depth}px;
    --wick-top: ${dims.wickTop}px;
    --wick-bottom: ${dims.wickBottom}px;
  `;

  return `
    <div class="candle-3d-container" style="${cssVars}">
      <div class="candle-3d">
        <!-- Top wick (3D box) -->
        <div class="wick-3d wick-3d-top">
          <div class="wick-face front"></div>
          <div class="wick-face back"></div>
          <div class="wick-face left"></div>
          <div class="wick-face right"></div>
        </div>

        <!-- Candle body -->
        <div class="candle-body-3d">
          <div class="candle-face front"></div>
          <div class="candle-face back"></div>
          <div class="candle-face left"></div>
          <div class="candle-face right"></div>
          <div class="candle-face top"></div>
          <div class="candle-face bottom"></div>
        </div>

        <!-- Bottom wick (3D box) -->
        <div class="wick-3d wick-3d-bottom">
          <div class="wick-face front"></div>
          <div class="wick-face back"></div>
          <div class="wick-face left"></div>
          <div class="wick-face right"></div>
        </div>

      </div>
    </div>
  `;
}

/**
 * Update existing candle dimensions via CSS variables
 */
export function updateCandleDimensions(
  container: HTMLElement,
  options: CandleOptions
): void {
  const { ohlc, changePercent, tier = 'common' } = options;

  const dims = calculateCandleDimensions(ohlc, changePercent, tier);
  const halfDepth = dims.depth / 2;

  const candleContainer = container.querySelector('.candle-3d-container') as HTMLElement;
  if (candleContainer) {
    candleContainer.style.setProperty('--candle-height', `${dims.bodyHeight}px`);
    candleContainer.style.setProperty('--candle-width', `${dims.width}px`);
    candleContainer.style.setProperty('--candle-depth', `${dims.depth}px`);
    candleContainer.style.setProperty('--candle-half-depth', `${halfDepth}px`);
    candleContainer.style.setProperty('--candle-side-width', `${dims.depth}px`);
    candleContainer.style.setProperty('--wick-top', `${dims.wickTop}px`);
    candleContainer.style.setProperty('--wick-bottom', `${dims.wickBottom}px`);
  }
}
